import { ApiError, isSessionEnded } from '@central/api';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import {
  Outlet,
  type RouterHistory,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import type { SSOErrorCode } from '@central/api';
import { api, meQuery } from './api';
import { Deployment } from './routes/Deployment';
import { Deployments } from './routes/Deployments';
import { Home } from './routes/Home';
import { Invite } from './routes/Invite';
import { LinkSso } from './routes/LinkSso';
import { Licenses } from './routes/Licenses';
import { Logs } from './routes/Logs';
import { Messages } from './routes/Messages';
import {
  parseListSearch,
  parseMessagesSearch,
  parseNewMessageSearch,
} from './routes/messagesSearch';
import { NewMessagePage } from './routes/NewMessage';
import { ForgotPassword, ResetPassword } from './routes/PasswordReset';
import { Releases } from './routes/Releases';
import { SignIn } from './routes/SignIn';
import { Team } from './routes/Team';
import { Thread } from './routes/Thread';
import { parseSsoError } from './sso';
import { AppLayout } from './shell/AppLayout';
import { navItems } from './shell/nav';

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Outlet });

// A signed-in visitor has nothing to do on the sign-in form. Any failure to
// read the session (401, network, 5xx) shows the form. The session is read
// outside the query cache: the cache's 401 handler sends the user here, and
// a cached me may belong to a session that has since ended.
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  validateSearch: (s: Record<string, unknown>): { ssoError?: SSOErrorCode } => ({
    ssoError: parseSsoError(s.ssoError),
  }),
  beforeLoad: async ({ context }) => {
    const me = await api.auth.me().catch(() => undefined);
    if (!me) return;
    context.queryClient.setQueryData(meQuery.queryKey, me);
    throw redirect({ to: '/' });
  },
  component: SignIn,
});

// A first SSO sign-in for an existing user lands here from the IdP callback.
const linkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in/link',
  component: LinkSso,
});

// An invite link lands here with its token in the fragment; no session.
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite',
  component: Invite,
});

// "Forgot password?" on sign-in; email carries over from the sign-in form.
const forgotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  validateSearch: (s: Record<string, unknown>): { email?: string } => ({
    email: typeof s.email === 'string' && s.email ? s.email.slice(0, 320) : undefined,
  }),
  component: ForgotPassword,
});

// An emailed reset link lands here with its token in the fragment; no session.
const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPassword,
});

// Every route under appRoute needs a full portal session; 401 sends the user to sign in.
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context }) => {
    try {
      const me = await context.queryClient.ensureQueryData(meQuery);
      return { me };
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) throw redirect({ to: '/sign-in' });
      throw e;
    }
  },
  component: AppLayout,
});

type Section = Exclude<(typeof navItems)[number], { to: '/messages' }>;

const screens: Record<Section['to'], () => ReactNode> = {
  '/': Home,
  '/deployments': Deployments,
  '/licenses': Licenses,
  '/releases': Releases,
  '/logs': Logs,
  '/team': Team,
};

const messagesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/messages',
  validateSearch: parseMessagesSearch,
  // A conversation link (emails, bookmarks) opens the conversation's own page
  // under the same list filters; a prefilled compose link opens the new-message page.
  beforeLoad: ({ search }) => {
    if (search.thread) {
      throw redirect({
        to: '/messages/$threadId',
        params: { threadId: search.thread },
        search: { show: search.show, q: search.q },
        replace: true,
      });
    }
    if (search.kind || search.subject || search.environmentId) {
      throw redirect({
        to: '/messages/new',
        search: { kind: search.kind, subject: search.subject, environmentId: search.environmentId },
        replace: true,
      });
    }
  },
  component: Messages,
});

const newMessageRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/messages/new',
  validateSearch: parseNewMessageSearch,
  component: NewMessagePage,
});

const threadRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/messages/$threadId',
  validateSearch: parseListSearch,
  component: Thread,
});

const sectionRoutes = navItems
  .filter((item): item is Section => item.to !== '/messages')
  .map((item) =>
    createRoute({
      getParentRoute: () => appRoute,
      path: item.to,
      component: screens[item.to],
    }),
  );

const deploymentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/deployments/$deploymentId',
  component: Deployment,
});

const signInPaths = new Set([
  '/sign-in',
  '/sign-in/link',
  '/invite',
  '/forgot-password',
  '/reset-password',
]);

const routeTree = rootRoute.addChildren([
  signInRoute,
  linkRoute,
  inviteRoute,
  forgotRoute,
  resetRoute,
  appRoute.addChildren([
    ...sectionRoutes,
    messagesRoute,
    newMessageRoute,
    threadRoute,
    deploymentRoute,
  ]),
]);

/**
 * The portal's query client and router, built together. A 401 unauthenticated from
 * any call made outside the sign-in screens means the session has ended: the cache is
 * cleared, so nothing of this customer outlives it, and the user is sent to sign in.
 * On /sign-in, /sign-in/link, /invite and the password-reset pages there is no session
 * to end. Any other 401 (invalid_credentials, invalid_code, link_expired,
 * password_required, invalid_token) is an inline answer.
 * A 4xx is an answer, never retried.
 * history defaults to the browser's; tests pass a memory history.
 */
export function createPortal(history?: RouterHistory) {
  const onError = (error: unknown) => {
    if (!isSessionEnded(error)) return;
    if (signInPaths.has(router.state.location.pathname)) return;
    queryClient.clear();
    void router.navigate({ to: '/sign-in' });
  };
  const queryClient = new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: (failures, error) =>
          !(error instanceof ApiError && error.status < 500) && failures < 3,
      },
    },
  });
  const router = createAppRouter(queryClient, history);
  return { queryClient, router };
}

function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    ...(history ? { history } : {}),
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
